import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import {
  createJoinRequest,
  listMine,
  cancelJoinRequest,
} from "../api/joinRequests";
import { searchSalonDirectory } from "../api/salons";

const STATUS_PILL_STYLES = {
  PENDING: "bg-amber-100 text-amber-700 border-amber-200",
  APPROVED: "bg-emerald-100 text-emerald-700 border-emerald-200",
  REJECTED: "bg-rose-100 text-rose-700 border-rose-200",
  CANCELED: "bg-slate-100 text-slate-600 border-slate-200",
  CANCELLED: "bg-slate-100 text-slate-600 border-slate-200",
  EXPIRED: "bg-orange-100 text-orange-600 border-orange-200",
};

const formatMmDdYyyy = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const normalizeStatus = (value) => (value || "").toUpperCase();

export default function JoinSalon() {
  const { token, accessibleSalons, refreshSession } = useAuth();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [requests, setRequests] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [statusMessageType, setStatusMessageType] = useState("success");
  const [cancellingRequestId, setCancellingRequestId] = useState(null);
  const [refreshingAccess, setRefreshingAccess] = useState(false);
  const resultsRef = useRef(null);
  const approvedSalonIds = useMemo(() => {
    const ids = new Set();
    (accessibleSalons || []).forEach((entry) => {
      const candidate = Number(entry?.salon_id ?? entry?.id);
      if (!Number.isNaN(candidate)) {
        ids.add(candidate);
      }
    });
    (requests || []).forEach((request) => {
      if (normalizeStatus(request.status) === "APPROVED") {
        const candidate = Number(request.salon_id);
        if (!Number.isNaN(candidate)) {
          ids.add(candidate);
        }
      }
    });
    return ids;
  }, [accessibleSalons, requests]);

  const salonRequestStatusMap = useMemo(() => {
    const priority = {
      PENDING: 0,
      APPROVED: 1,
      EXPIRED: 2,
      REJECTED: 3,
      CANCELED: 4,
      CANCELLED: 4,
    };
    const map = new Map();
    (requests || [])
      .filter((req) => req?.salon_id != null)
      .forEach((req) => {
        const salonId = Number(req.salon_id);
        if (Number.isNaN(salonId)) return;
        const status = normalizeStatus(req.status);
        const existing = map.get(salonId);
        if (!existing || (priority[status] ?? 99) < (priority[existing] ?? 99)) {
          map.set(salonId, status);
        }
      });
    return map;
  }, [requests]);

  const setStatusFeedback = useCallback((text, type = "success") => {
    setStatusMessage(text);
    setStatusMessageType(type);
  }, []);

  const refreshRequests = useCallback(async () => {
    if (!token) return [];
    const data = await listMine(token);
    return Array.isArray(data) ? data : [];
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let active = true;
    setRequestsLoading(true);
    setError("");
    refreshRequests()
      .then((data) => {
        if (!active) return;
        setRequests(data);
      })
      .catch((err) => {
        if (!active) return;
        console.error(err);
        setError(err.message);
        setRequests([]);
      })
      .finally(() => {
        if (!active) return;
        setRequestsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [refreshRequests, token]);

  useEffect(() => {
    if (!token) return undefined;
    const hasPending = (requests || []).some(
      (request) => normalizeStatus(request.status) === "PENDING"
    );
    if (!hasPending) return undefined;
    const intervalId = window.setInterval(() => {
      refreshRequests()
        .then((data) => {
          setRequests(data);
        })
        .catch(() => {});
      refreshSession?.().catch(() => {});
    }, 30000);
    return () => window.clearInterval(intervalId);
  }, [refreshRequests, refreshSession, requests, token]);

  const loadDirectory = useCallback(async (params = {}) => {
    setSearchLoading(true);
    setError("");
    setResults([]);
    try {
      const data = await searchSalonDirectory(params);
      if (Array.isArray(data)) {
        setResults(data);
      } else {
        setResults([]);
      }
    } catch (err) {
      console.error(err);
      setError(err?.message || "Unable to load salons");
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDirectory();
  }, [loadDirectory]);

  const handleSearch = async (event) => {
    event.preventDefault();
    const trimmed = query.trim();
    const params = trimmed ? { q: trimmed } : {};
    await loadDirectory(params);
  };

  const handleRequestJoin = async (salonId) => {
    if (!token) return;
    setError("");
    setStatusMessage("");
    setStatusMessageType("success");
    const numericSalonId = Number(salonId);
    if (approvedSalonIds.has(numericSalonId)) {
      setStatusFeedback("You are already approved for this salon.", "info");
      setMessage("");
      return;
    }
    try {
      await createJoinRequest(token, salonId, message);
      setStatusFeedback("Join request sent.", "success");
    } catch (err) {
      const detail = (err.message || "").toLowerCase();
      if (
        detail.includes("already a member") ||
        detail.includes("already have access")
      ) {
        setStatusFeedback("You are already approved for this salon.", "info");
      } else {
        setError(err.message);
      }
    } finally {
      try {
        const refreshed = await refreshRequests();
        setRequests(refreshed);
      } catch (err) {
        console.error(err);
      }
      setMessage("");
    }
  };

  const handleCancelRequest = async (request) => {
    if (!token) return;
    const confirmed = window.confirm(
      "Are you sure you want to cancel this pending request?"
    );
    if (!confirmed) return;
    setError("");
    setStatusMessage("");
    setStatusMessageType("success");
    setCancellingRequestId(request.id);
    try {
      await cancelJoinRequest(token, request.id);
      setStatusFeedback("Request canceled.", "success");
    } catch (err) {
      setError(err.message);
    } finally {
      try {
        const refreshed = await refreshRequests();
        setRequests(refreshed);
      } catch (err) {
        console.error(err);
      }
      setCancellingRequestId(null);
    }
  };

  const handleActionClick = (salonIdValue, normalizedStatus) => {
    if (normalizedStatus === "APPROVED") {
      setStatusFeedback(
        "You are already joined and approved for this salon.",
        "info"
      );
      return;
    }
    if (normalizedStatus === "PENDING") {
      setStatusFeedback(
        "You already have a pending request for this salon.",
        "info"
      );
      return;
    }
    handleRequestJoin(salonIdValue);
  };

  const handleRequestAgain = async (request) => {
    const prefill = (request.salon_name || request.salon_city || "").trim();
    const params = prefill ? { q: prefill } : {};
    setQuery(prefill);
    setMessage(request.message || "");
    setError("");
    await loadDirectory(params);
    if (resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleRefreshAccess = async () => {
    setRefreshingAccess(true);
    setError("");
    try {
      const [refreshedRequests] = await Promise.all([
        refreshRequests(),
        refreshSession?.(),
      ]);
      setRequests(refreshedRequests);
      setStatusFeedback("Access refreshed. If a salon approved you, it will appear now.", "success");
    } catch (err) {
      setError(err?.message || "Unable to refresh access right now.");
    } finally {
      setRefreshingAccess(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 py-12">
      <div className="max-w-4xl mx-auto px-4 space-y-8">
        <div>
          <h1 className="text-3xl font-semibold">Join a salon</h1>
          <p className="text-sm text-slate-500 mt-1">
            Search salons and request access. Managers will review your request and
            you&apos;ll receive a notification when it is approved.
          </p>
          <div className="mt-3">
            <button
              type="button"
              onClick={handleRefreshAccess}
              disabled={refreshingAccess}
              className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-70"
            >
              {refreshingAccess ? "Refreshing..." : "Refresh access"}
            </button>
          </div>
        </div>

        <form
          className="bg-white border border-slate-200 rounded-lg p-6 space-y-4"
          onSubmit={handleSearch}
        >
          <label className="text-sm font-medium text-slate-700">
            Salon name or location
          </label>
          <div className="flex gap-2">
            <input
              className="flex-1 rounded border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-rose-300"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by salon name, city, address, or Salon ID"
            />
            <button
              type="submit"
              disabled={searchLoading}
              className="px-4 py-2 rounded bg-rose-600 text-white text-sm font-semibold disabled:opacity-70"
            >
              {searchLoading ? "Searching..." : "Search"}
            </button>
          </div>
          <label className="text-sm font-medium text-slate-700">
            Optional message to the manager
          </label>
          <textarea
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-rose-300"
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Introduce yourself or explain why you'd like access."
          />
        </form>

        {statusMessage && (
          <div
            className={`rounded border px-4 py-3 text-sm ${
              statusMessageType === "success"
                ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                : "bg-amber-50 border-amber-200 text-amber-700"
            }`}
          >
            {statusMessage}
          </div>
        )}
        {error && (
          <div className="rounded bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        <section className="space-y-4" ref={resultsRef}>
          <h2 className="text-xl font-semibold">Search results</h2>
          {results.length === 0 && !searchLoading && (
            <p className="text-sm text-slate-500">
              No salons found. Try another search or clear the filter.
            </p>
          )}
          {results.length > 0 && (
            <div className="overflow-x-auto">
                <div className="min-w-[780px] border border-slate-100 rounded-lg">
                  <div className="grid grid-cols-[70px_2.4fr_2.6fr_110px_120px_150px] gap-3 px-4 py-3 text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                  <span>Salon ID</span>
                  <span>Salon Name</span>
                  <span>Location</span>
                  <span>City</span>
                  <span>Status</span>
                  <span>Request to join</span>
                </div>
                {results.map((salon) => {
                  const salonIdValue = Number(salon.id);
                  const status = salonRequestStatusMap.get(salonIdValue);
                  const isApproved = status === "APPROVED" || approvedSalonIds.has(salonIdValue);
                  const normalizedStatus = status || (isApproved ? "APPROVED" : undefined);
                  const pillClasses =
                    STATUS_PILL_STYLES[normalizedStatus] ||
                    "bg-slate-100 text-slate-600 border-slate-200";
                  const locationText =
                    salon.address ||
                    [salon.city, salon.province].filter(Boolean).join(", ") ||
                    "Location not provided";
                  const cityText = salon.city || "Unknown";
                  return (
                    <div
                      key={salon.id}
                      className="grid grid-cols-[70px_2.4fr_2.6fr_110px_120px_150px] gap-3 items-center px-4 py-4 border-b border-slate-100 hover:bg-slate-50 last:border-b-0"
                    >
                      <span className="text-sm text-slate-600">{salonIdValue}</span>
                      <strong
                        className="text-sm font-semibold text-slate-900 truncate whitespace-nowrap"
                        title={salon.name || "Salon"}
                      >
                        {salon.name || "Salon"}
                      </strong>
                      <span
                        className="text-sm text-slate-600 truncate whitespace-nowrap"
                        title={locationText}
                      >
                        {locationText}
                      </span>
                      <span className="text-sm text-slate-600">{cityText}</span>
                      <div className="flex flex-col gap-1">
                        {normalizedStatus ? (
                          <span
                            className={`text-[11px] font-semibold uppercase tracking-wide border px-2 py-1 rounded ${pillClasses}`}
                          >
                            {normalizedStatus}
                          </span>
                        ) : (
                          <span className="text-[11px] text-slate-500">
                            Awaiting status
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-end">
                        <button
                          type="button"
                          onClick={() =>
                            handleActionClick(salonIdValue, normalizedStatus)
                          }
                          className={`px-4 py-2 text-sm font-semibold rounded ${
                            normalizedStatus === "APPROVED"
                              ? "bg-slate-100 text-slate-600 border border-slate-200"
                              : normalizedStatus === "PENDING"
                              ? "bg-amber-50 text-amber-700 border border-amber-200"
                              : "bg-rose-600 text-white hover:bg-rose-700"
                          }`}
                          disabled={searchLoading}
                        >
                          {normalizedStatus === "PENDING"
                            ? "Requested"
                            : "Request to join"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
          <h2 className="text-xl font-semibold">Pending requests</h2>
          {requestsLoading ? (
            <p className="text-sm text-slate-500">Loading your requests...</p>
          ) : requests.length === 0 ? (
            <p className="text-sm text-slate-500">
              You haven&apos;t submitted any requests yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <div className="min-w-[720px] border border-slate-100 rounded-lg">
                <div className="grid grid-cols-[70px_2.8fr_140px_120px_160px] px-4 py-3 text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                  <span>Salon ID</span>
                  <span>Salon Name</span>
                  <span>Requested on</span>
                  <span>Status</span>
                  <span>Cancel request</span>
                </div>
                {requests.map((request, index) => {
                  const normalizedStatus = normalizeStatus(request.status);
                  const pillClasses =
                    STATUS_PILL_STYLES[normalizedStatus] ||
                    "bg-slate-100 text-slate-600 border-slate-200";
                  const isPending = normalizedStatus === "PENDING";
                  return (
                    <div
                      key={request.id}
                      className={`grid grid-cols-[70px_2.8fr_140px_120px_160px] gap-3 items-center px-4 py-4 border-slate-100 hover:bg-slate-50 ${
                        index !== requests.length - 1 ? "border-b" : ""
                      }`}
                    >
                      <span className="text-sm text-slate-600">
                        {request.salon_id ?? "—"}
                      </span>
                      <div className="truncate whitespace-nowrap" title={request.salon_name || "Salon"}>
                        <strong className="text-sm font-semibold text-slate-900">
                          {request.salon_name || "Salon"}
                        </strong>
                        {request.message && (
                          <p className="text-[12px] text-slate-500 mt-1">
                            {request.message}
                          </p>
                        )}
                        {normalizedStatus === "EXPIRED" && (
                          <p className="text-[11px] text-orange-600 mt-1">
                            Expired (no response in 14 days). You can request again.
                          </p>
                        )}
                      </div>
                      <span className="text-sm text-slate-600">
                        {formatMmDdYyyy(request.created_at)}
                      </span>
                      <div className="flex flex-col gap-1">
                        <span
                          className={`text-[11px] font-semibold uppercase tracking-wide border px-2 py-1 rounded ${pillClasses}`}
                        >
                          {normalizedStatus}
                        </span>
                        {normalizedStatus === "EXPIRED" && (
                          <button
                            type="button"
                            onClick={() => handleRequestAgain(request)}
                            className="text-[11px] text-rose-600 font-semibold hover:underline text-left"
                          >
                            Request again
                          </button>
                        )}
                      </div>
                      <div>
                        {isPending ? (
                          <button
                            type="button"
                            disabled={cancellingRequestId === request.id}
                            onClick={() => handleCancelRequest(request)}
                            className="px-3 py-1.5 rounded border border-slate-300 text-sm font-semibold disabled:opacity-70"
                          >
                            {cancellingRequestId === request.id
                              ? "Canceling..."
                              : "Cancel request"}
                          </button>
                        ) : (
                          <span className="text-[13px] text-slate-400">—</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
